import * as React from 'react';
import { useLocation } from 'react-router';

import { parseUrl } from 'query-string';

import { Api, EBillingInfoResponseResult, IBillingInfo } from '@api';

import { Log } from '@base/utils';

import { EPaymentState, IPaymentData } from './PaymentResult';

import { IUser } from '@interfaces';

export interface IPaymentResultReducer {
	state: Partial<IPaymentData>;
	action: IPaymentResultAction;
}

export interface IPaymentResultAction {
	type: EPaymentState;
	payload?: IBillingInfo;
}

export function PaymentReducer(state: Partial<IPaymentData>, action: IPaymentResultAction): IPaymentData {
	switch (action.type) {
		case EPaymentState.SUCCESS:
			return { status: EPaymentState.SUCCESS, ...action.payload };
		case EPaymentState.ERROR:
			return { status: EPaymentState.ERROR, ...action.payload };
		case EPaymentState.PENDING:
			return { status: EPaymentState.PENDING, ...action.payload };
		default:
			throw new Error();
	}
}

export const usePaymentResultReducer = (currentUser: IUser) => {
	const { search } = useLocation();
	const { query: { showsuccess, showfail, cond, issuer_id: issuerId = '' } } = parseUrl(search);
	const {
		mailiki_price: price = '',
		app_id: appId = '',
		service_name: serviceName = '',
		mobile_canvas: isHybrid = '',
	} = cond && cond.length ? JSON.parse(cond as string) : {};
	const paymentResultData = {
		price: Number(price),
		appId,
		serviceName,
		isHybrid: Boolean(Number(isHybrid)),
	};
	const [paymentData, dispatch] = React.useReducer(PaymentReducer, { status: EPaymentState.PENDING, ...paymentResultData });

	const tries = React.useRef(10);
	const dispatchError = () => dispatch({ type: EPaymentState.ERROR, payload: paymentResultData });
	const waitStatus = (timeOut = 0) => {
		setTimeout(() => {
			Api.billing.getStatByIssuerId(currentUser, issuerId as string)
				.then(({ status }) => {
					switch (status) {
						case EBillingInfoResponseResult.SUCCESS:
							dispatch({ type: EPaymentState.SUCCESS, payload: paymentResultData });
							break;
						case EBillingInfoResponseResult.ERROR:
							Log.error(`[billing] Can't get billing data;`);
							dispatchError();
							break;
						default:
							if (tries.current > 0) {
								tries.current--;
								waitStatus(1000);
							} else {
								dispatchError();
							}
							break;
					}
				})
				.catch((err) => {
					Log.error(`[billing] Can't get billing data;`, err);
					dispatchError();
				});
		}, timeOut);
	};

	React.useEffect(() => {
		if (showsuccess === '1') {
			waitStatus();
		} else if (showfail === '1') {
			dispatchError();
		}
	}, [paymentData.status]);

	return { paymentData, dispatch };
};
